import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProjectDetailReopen } from './config';

/**
 * Put the project/stage detail modal back after a round trip through a
 * full-screen route.
 *
 * Opening a document from the modal leaves the page entirely, and the modal is
 * component state rather than a route, so browser Back returns to a bare
 * register with the user's place gone. The departing navigation therefore
 * carries instructions in history state, and the page that owns the modal picks
 * them up here on arrival.
 *
 * The instruction is spent once read: a later refresh, or Back onto this entry,
 * should land on the plain page rather than silently reopening a modal the user
 * has since closed.
 *
 * Shared by every page that renders the modal (the project register, the
 * workflow board, timesheets) so returning behaves the same wherever the
 * document was opened from.
 */
export function useProjectDetailReopen(onReopen: (reopen: ProjectDetailReopen) => void): void {
  const location = useLocation();
  const navigate = useNavigate();

  // Held in a ref so the effect depends on the location alone. Callers would
  // otherwise have to memoise the callback to avoid re-running it.
  const onReopenRef = useRef(onReopen);
  onReopenRef.current = onReopen;

  useEffect(() => {
    const reopen = (location.state as { reopen?: ProjectDetailReopen } | null)?.reopen;
    if (!reopen) return;

    onReopenRef.current(reopen);
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null }
    );
  }, [location.state, location.pathname, location.search, navigate]);
}
